import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Play, Pause, SkipBack, Music2, Layers, Download, RefreshCw, Info, User, Edit } from 'lucide-react';
import { SoundSignalVisualizer, SoundSignalVisualizerRef } from '../../components/ui/SoundSignalVisualizer';
import { SongSectionsManager } from '../../components/catalogue/SongSectionsManager';
import { BuildProgressionModal } from '../../components/catalogue/BuildProgressionModal';
import { StickyPlayer } from '../../components/ui/StickyPlayer';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../../constants/api';
import { Project } from '../../types/project';
import { useAuth } from '../../hooks/useAuth';
import { useSoundworlds } from '../../hooks/useSoundworlds';
import toast from 'react-hot-toast';
import { useCredits } from '../../contexts/CreditsContext';
// import { useWebSocket } from '../../hooks/useWebSocket';

export const GeneratorPage: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth0();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const locationPath = useLocation();
    const { name } = useAuth();
    const { soundworlds, renderStemsByPieceId } = useSoundworlds();
    const { credits, refreshCredits } = useCredits();
    // const { isConnected: isWebSocketConnected } = useWebSocket();

    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [stemsLoading, setStemsLoading] = useState(false);
    const [bpm, setBpm] = useState(120);
    const [bpmInput, setBpmInput] = useState('120');
    const [bpmAuto, setBpmAuto] = useState(true);
    const [chords, setChords] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const [isProgressionModalOpen, setIsProgressionModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'horizontal' | 'vertical'>('vertical');
    const audioRef = useRef<SoundSignalVisualizerRef>(null);
    const [formData, setFormData] = useState({
        soundworlds: [] as string[],
        tempo: '120',
        chords: '',
        form: [] as Array<{
            name: string;
            bars: number;
            chords?: string;
            elements: string[];
        }>
    });

    const secondsPerBar = bpm > 0 ? 240 / bpm : 0;
    const sections = useMemo(() => {
        if (secondsPerBar <= 0) return undefined;
        const result: Array<{ start: number; end: number; label: string }> = [];
        let currentTime = 0;
        formData.form.forEach((section, index) => {
            const bars = section.bars || 0;
            const sectionDuration = bars * secondsPerBar;
            if (sectionDuration > 0) {
                result.push({
                    start: currentTime,
                    end: currentTime + sectionDuration,
                    label: section.name || `Section ${index + 1}`
                });
                currentTime += sectionDuration;
            }
        });
        return result.length > 0 ? result : undefined;
    }, [secondsPerBar, formData.form]);

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    const currentSoundworld = project?.form?.soundworlds?.[0] ?
        soundworlds.find(sw => sw.name === project.form.soundworlds[0]) :
        soundworlds.length > 0 ? soundworlds[0] : undefined;

    const hasSoundworld = project?.form?.soundworlds?.[0] && currentSoundworld;

    const filterEmptyFields = (data: any) => {
        const filtered: any = {};

        if (data.soundworlds && data.soundworlds.length > 0 && hasSoundworld) {
            filtered.soundworlds = data.soundworlds;
        }

        // Не включаем tempo если включен auto режим
        if (!bpmAuto && data.tempo && data.tempo.trim() !== '') {
            filtered.tempo = data.tempo;
        }

        if (data.chords && data.chords.trim() !== '') {
            filtered.chords = data.chords;
        }

        if (data.form && data.form.length > 0 && (data.form.length > 1 || data.form[0].elements.length > 0)) {
            const filteredForm = data.form.filter((section: any) => {
                if (!section.name || !section.bars || section.bars <= 0) return false;

                if (section.elements && section.elements.length > 0) {
                    section.elements = section.elements.filter((element: string) =>
                        element && element.trim() !== ''
                    );
                }

                if (!section.elements || section.elements.length === 0) {
                    delete section.elements;
                }

                if (section.chords && section.chords.trim() === '') {
                    delete section.chords;
                }

                return true;
            });

            if (filteredForm.length > 0) {
                filtered.form = filteredForm;
            }
        }

        return filtered;
    };

    const handleFormDataChange = useCallback((newFormData: {
        soundworlds: string[];
        tempo: string;
        chords: string;
        form: Array<{
            name: string;
            bars: number;
            chords?: string;
            elements: string[];
        }>;
    }) => {
        setFormData({
            ...newFormData,
            soundworlds: hasSoundworld ? newFormData.soundworlds : [],
            tempo: bpmAuto ? '' : bpm.toString(),
            chords: chords
        });
    }, [bpm, chords, bpmAuto, hasSoundworld]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            soundworlds: hasSoundworld ? prev.soundworlds : [],
            tempo: bpmAuto ? '' : bpm.toString(),
            chords: chords
        }));
    }, [bpm, chords, bpmAuto, hasSoundworld]);

    const loadProject = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_PROJECTS}`, {
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error('Failed to load project');
            }

            const projects = await res.json();
            const currentProject = projects.find((p: Project) => p.project_id === projectId);

            if (currentProject) {
                setProject(currentProject);

                if (currentProject.form) {
                    const savedForm = currentProject.form;


                    if (savedForm.tempo) {
                        const newBpm = parseInt(savedForm.tempo) || 120;
                        setBpm(newBpm);
                        setBpmInput(newBpm.toString());
                        // Если есть сохраненный tempo, значит auto был выключен
                        setBpmAuto(false);
                    } else {
                        // Если tempo нет, значит был включен auto режим
                        setBpmAuto(true);
                    }
                    if (savedForm.chords) {
                        setChords(savedForm.chords);
                    }

                    setFormData({
                        soundworlds: savedForm.soundworlds || [],
                        tempo: savedForm.tempo || '',
                        chords: savedForm.chords || '',
                        form: savedForm.form || []
                    });
                } else if (currentProject.form?.soundworlds?.[0]) {
                    setFormData({
                        soundworlds: [currentProject.form.soundworlds[0]],
                        tempo: '',
                        chords: '',
                        form: []
                    });
                } else {
                    setFormData({
                        soundworlds: [],
                        tempo: '',
                        chords: '',
                        form: []
                    });
                }

                if (currentProject.current_piece_id && !currentProject.audio_url) {
                    const audioUrl = `https://cdn.vesperus.tech/generated/testing/${currentProject.current_piece_id}.mp3`;
                    setProject(prev => prev ? {
                        ...prev,
                        audio_url: audioUrl
                    } : null);
                }
            } else {
                toast.error('Project not found');
                navigate('/explore');
            }
        } catch (error) {
            console.error('Error loading project:', error);
            toast.error('Failed to load project');
            navigate('/explore');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) {
            loadProject();
        }
    }, [projectId, navigate]);

    // WebSocket listener for generation status
    useEffect(() => {
        const handleAudioReady = (event: CustomEvent) => {
            const { project_id } = event.detail;
            if (project_id === projectId) {
                setGenerating(false);
                toast.success('Music generation completed!');
                // Reload project to get updated audio_url
                loadProject();
                refreshCredits();
            }
        };

        const handlePreviewReady = (event: CustomEvent) => {
            const { pieceId, url } = event.detail;
            if (project && (project.piece_id === pieceId || project.current_piece_id === pieceId)) {
                setGenerating(false);
                toast.success('Music generation completed!');
                setProject(prev => prev ? {
                    ...prev,
                    audio_url: url,
                    current_piece_id: pieceId
                } : null);
                refreshCredits();
            }
        };

        const handleSoundworldStemsReady = (event: CustomEvent) => {
            const { project_id, pieceId, url } = event.detail;
            if (project_id === projectId || (project && (project.piece_id === pieceId || project.current_piece_id === pieceId))) {
                setGenerating(false);
                setStemsLoading(false);
                toast.success('Soundworld stems are ready!');

                if (url) {
                    handleDownload(url, 'project-stems.zip');
                }

                loadProject();
                refreshCredits();
            }
        };

        const handleJobError = (event: CustomEvent) => {
            const { project_id, pieceId, message } = event.detail;
            if (project_id === projectId || (project && (project.piece_id === pieceId || project.current_piece_id === pieceId))) {
                setGenerating(false);
                setStemsLoading(false);
                toast.error(`Generation failed: ${message}`);
            }
        };

        window.addEventListener('audioReady', handleAudioReady as EventListener);
        window.addEventListener('previewReady', handlePreviewReady as EventListener);
        window.addEventListener('soundworldStemsReady', handleSoundworldStemsReady as EventListener);
        window.addEventListener('jobError', handleJobError as EventListener);

        return () => {
            window.removeEventListener('audioReady', handleAudioReady as EventListener);
            window.removeEventListener('previewReady', handlePreviewReady as EventListener);
            window.removeEventListener('soundworldStemsReady', handleSoundworldStemsReady as EventListener);
            window.removeEventListener('jobError', handleJobError as EventListener);
        };
    }, [projectId, project]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, [locationPath.pathname]);

    const handleGenerate = async () => {
        if (!project || !hasSoundworld || !currentSoundworld) return;

        setGenerating(true);
        try {
            // if (!isWebSocketConnected) {
            //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
            //     setGenerating(false);
            //     return;
            // }
            const filteredData = filterEmptyFields({
                soundworlds: [project.form.soundworlds[0]],
                tempo: bpmAuto ? '' : bpm.toString(),
                chords: chords,
                form: formData.form
            });

            const params = new URLSearchParams({
                project_id: project.project_id
            });

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_GENERATE}?${params.toString()}`,
                {
                    method: 'POST',
                    headers: {
                        ...getAuthHeadersWithoutCredentials(),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        tags: JSON.stringify(filteredData)
                    }),
                }
            );

            if (!res.ok) {
                throw new Error("Failed to start generation");
            }

            const result = await res.json();

            if (result.piece_id) {
                setProject(prev => prev ? {
                    ...prev,
                    piece_id: result.piece_id
                } : null);
            }

            toast.success("Generation started!");
        } catch (error) {
            setGenerating(false);
            toast.error("Failed to start generation");
        }
    };

    const handleGenerateAgain = async () => {
        if (!project || !hasSoundworld || !currentSoundworld) return;

        setGenerating(true);
        try {
            // if (!isWebSocketConnected) {
            //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
            //     setGenerating(false);
            //     return;
            // }
            const filteredData = filterEmptyFields({
                soundworlds: [project.form.soundworlds[0]],
                tempo: bpmAuto ? '' : bpm.toString(),
                chords: chords,
                form: formData.form
            });

            const params = new URLSearchParams({
                project_id: project.project_id
            });

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_GENERATE}?${params.toString()}`,
                {
                    method: 'POST',
                    headers: {
                        ...getAuthHeadersWithoutCredentials(),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        tags: JSON.stringify(filteredData)
                    }),
                }
            );

            if (!res.ok) {
                throw new Error("Failed to start regeneration");
            }

            const result = await res.json();

            if (result.piece_id) {
                setProject(prev => prev ? {
                    ...prev,
                    piece_id: result.piece_id
                } : null);
            }

            toast.success("Regeneration started!");
        } catch (error) {
            setGenerating(false);
            toast.error("Failed to start regeneration");
        }
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            toast.success('Download started');
        } catch (error) {
            toast.error('Download failed');
        }
    };

    const handleGetMidiUrl = async () => {
        if (!project?.current_piece_id && !project?.piece_id) {
            toast.error('No piece ID available');
            return;
        }

        try {
            const pieceId = project.current_piece_id || project.piece_id;
            const response = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_GET_MIDI_URL}?piece_id=${pieceId}`,
                {
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!response.ok) throw new Error('Failed to get MIDI URL');

            const data = await response.json();
            if (data.url) {
                await handleDownload(data.url, 'project-midi.mid');
            } else {
                toast.error('MIDI URL not available');
            }
        } catch (error) {
            toast.error('Failed to get MIDI file');
        }
    };

    const handleGetStems = async () => {
        if (!project?.current_piece_id && !project?.piece_id) {
            toast.error('No piece ID available');
            return;
        }

        setStemsLoading(true);
        try {
            // if (!isWebSocketConnected) {
            //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
            //     setStemsLoading(false);
            //     return;
            // }
            const pieceId = project.current_piece_id || project.piece_id;
            console.log('Starting stems rendering for pieceId:', pieceId);

            await renderStemsByPieceId(pieceId);
            toast.success('Stems rendering started! You will be notified when ready.');
        } catch (error) {
            console.error('Error starting stems rendering:', error);
            toast.error('Failed to start stems rendering');
            setStemsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!project) {
        return null;
    }

    return (
        <div className="h-screen flex">
            {/* Right Content - скроллируемый */ }
            <div className="flex-1 overflow-y-scroll pb-56">
                {/* Header with user info and credits */ }
                <div className="w-full border-b border-border mt-[56.5px] md:mt-[-3.5px]">
                    <div className="relative flex items-center gap-4 px-4 py-5">
                        <div className="flex items-center gap-4 flex-shrink-0 min-w-0">
                            <button
                                onClick={ () => navigate('/explore') }
                                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-card-foreground hover:underline flex-shrink-0"
                            >
                                <ArrowLeft size={ 16 } />
                                Back to Projects
                            </button>
                        </div>

                        <div className="hidden md:flex absolute inset-x-0 justify-center pointer-events-none">
                            <div className="flex items-baseline gap-2 min-w-0 text-sm pointer-events-auto">
                                <span className="text-[9.4px] uppercase tracking-wide text-muted-foreground whitespace-nowrap leading-none">
                                    Current project:
                                </span>
                                <span className="font-medium text-card-foreground truncate max-w-[220px] leading-none">
                                    { project?.name || 'Untitled Project' }
                                </span>
                                { project?.description && (
                                    <span className="text-xs text-muted-foreground truncate max-w-[280px] leading-none">
                                        { project.description }
                                    </span>
                                ) }
                                { hasSoundworld && (
                                    <span className="inline-flex items-baseline gap-1 px-2 py-0.5 rounded-full bg-muted text-[11px] text-muted-foreground max-w-[240px] truncate leading-none">
                                        <span>Soundworld:</span>
                                        <span className="text-card-foreground font-medium truncate leading-none">
                                            { project.form.soundworlds[0] }
                                        </span>
                                    </span>
                                ) }
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                            <User size={ 16 } className="text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{ name || 'User' }</span>
                            <div className="ml-2 px-3 py-1 rounded-full bg-muted text-sm font-medium text-card-foreground">
                                { typeof credits === 'number' ? `${credits} credits` : '...' }
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Song Sections */ }
                    <div className="bg-card rounded-lg p-6 border border-border">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-card-foreground">Song Structure</h2>
                            { hasSoundworld && (
                                <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode('vertical')}
                                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                                            viewMode === 'vertical'
                                                ? 'bg-background text-card-foreground shadow-sm'
                                                : 'text-muted-foreground hover:text-card-foreground'
                                        }`}
                                    >
                                        Horizontal
                                    </button>
                                    <button
                                        onClick={() => setViewMode('horizontal')}
                                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                                            viewMode === 'horizontal'
                                                ? 'bg-background text-card-foreground shadow-sm'
                                                : 'text-muted-foreground hover:text-card-foreground'
                                        }`}
                                    >
                                        Vertical
                                    </button>
                                </div>
                            ) }
                        </div>
                        { hasSoundworld ? (
                            <SongSectionsManager
                                soundworld={ currentSoundworld }
                                initialFormData={ project?.form?.form }
                                onFormDataChange={ handleFormDataChange }
                                viewMode={ viewMode }
                                onViewModeChange={ setViewMode }
                            />
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <p>Song structure configuration is not available without a soundworld.</p>
                            </div>
                        ) }
                    </div>
                </div>
            </div>

            <BuildProgressionModal
                isOpen={ isProgressionModalOpen }
                onClose={ () => setIsProgressionModalOpen(false) }
                initialChords={ chords }
                onSave={ (newChords) => {
                    setChords(newChords);
                    setIsProgressionModalOpen(false);
                } }
            />
            <StickyPlayer
                title={project.name || 'Untitled Project'}
                description={project.description || undefined}
                isPlaying={isPlaying}
                onTogglePlay={() => audioRef.current?.toggle()}
                onRewind={() => audioRef.current?.rewind()}
                audioUrl={project.audio_url || undefined}
                bpm={bpm}
                bpmInput={bpmInput}
                bpmAuto={bpmAuto}
                onBpmAutoChange={(value) => setBpmAuto(value)}
                onBpmDecrease={() => {
                    const newBpm = Math.max(60, bpm - 1);
                    setBpm(newBpm);
                    setBpmInput(newBpm.toString());
                }}
                onBpmIncrease={() => {
                    const newBpm = Math.min(200, bpm + 1);
                    setBpm(newBpm);
                    setBpmInput(newBpm.toString());
                }}
                onBpmInputChange={(value) => {
                    setBpmInput(value);
                    const numValue = parseInt(value);
                    if (!isNaN(numValue) && numValue >= 60 && numValue <= 200) {
                        setBpm(numValue);
                    }
                }}
                onBpmInputBlur={() => {
                    const numValue = parseInt(bpmInput);
                    if (isNaN(numValue) || numValue < 60 || numValue > 200) {
                        setBpmInput(bpm.toString());
                    }
                }}
                hasSoundworld={Boolean(hasSoundworld)}
                hasAudio={Boolean(project.audio_url)}
                generating={generating}
                stemsLoading={stemsLoading}
                onGenerate={handleGenerate}
                onGenerateAgain={handleGenerateAgain}
                onGetMidi={handleGetMidiUrl}
                onGetStems={handleGetStems}
                onDownloadMp3={() => handleDownload(project.audio_url || '', 'project-master.mp3')}
                audioRef={audioRef}
                soundworldName={hasSoundworld ? project.form.soundworlds[0] : undefined}
                chords={chords}
                onChordsChange={(value) => setChords(value)}
                onOpenChordEditor={() => setIsProgressionModalOpen(true)}
                sections={sections}
            />
        </div>
    );
};